/* Core & libraries */
import React, { useState, useMemo } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage, useIntl } from "react-intl";
import { Field } from "formik";

/* Helpers, constants, custom methods, internal libraries */
import { aaPolicyCabinOptions, AIRLINES } from '../../../constants/appConstants.js';
import { getManagingTravelPolicyLink } from "helpers/general";
import { useUserContext } from "contexts/userContext";

/* Components */
import UCButton from "../../../components/buttons/index.js";
import UCToggle from "../../../components/uctoggle/index.js";
import UCSelect from "../../../components/ucselect/index.js";

/* Styles */
import styles, { lengthSelect, selectField } from "../styles.js";

const FlightDurationCard = ({
  values,
  hasMediumFlight,
  setFieldValue,
  setHasMedium,
  userHasEditPermission
}) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const [editDuration, setEditDuration] = useState(false);

  const flightDurationOptions = Array.from({ length: 20 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString(),
  }));

  const intl = useIntl();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode

  const managingTravelPolicyLink = useMemo(() => getManagingTravelPolicyLink(providerCode),[providerCode]);
  const durationCabinSubtitle = intl.formatMessage({
    id: providerCode === AIRLINES.AS ? "editPolicy.durationCabinSubtitle.AS" : "editPolicy.durationCabinSubtitle",
  }, {
    link: link => `<a href="${managingTravelPolicyLink}" target="_blank">${link}</a>`
  });

  return (
    <div className={classes.policyContainer}>
      <h3>
        <FormattedMessage
          id={
            providerCode === AIRLINES.AS
              ? 'editPolicy.durationTitle.AS'
              : 'editPolicy.durationTitle'
          }
        />
      </h3>
      <p>
        <FormattedMessage
          id={
            providerCode === AIRLINES.AS
              ? 'editPolicy.durationSubtitle.AS'
              : 'editPolicy.durationSubtitle'
          }
        />
      </p>
      <div className={classes.policyCard}>
        {editDuration && (
          <>
            <div className={classes.mediumFlightsContainer}>
              <UCToggle
                label={
                  <FormattedMessage
                    id={
                      providerCode === AIRLINES.AS
                        ? 'editPolicy.durationMediumToggle.AS'
                        : 'editPolicy.durationMediumToggle'
                    }
                  />
                }
                icons={null}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setHasMedium(isChecked);
                  const maxOptionValue = Math.max(
                    ...flightDurationOptions.map((option) => option.value),
                  );
                  if (isChecked && values.shortDurationValue === values.longDurationValue) {
                    const newLongDurationValue =
                      values.shortDurationValue + 1 > maxOptionValue
                        ? values.shortDurationValue
                        : values.shortDurationValue + 1;
                    setFieldValue('longDurationValue', newLongDurationValue);
                    if (values.shortDurationValue + 1 > maxOptionValue) {
                      setFieldValue('shortDurationValue', values.shortDurationValue - 1);
                    }
                  } else if (!isChecked) {
                    setFieldValue('longDurationValue', values.shortDurationValue);
                  }
                }}
                defaultChecked={hasMediumFlight}
              />
              <div className={classes.mediumFlightsBox}>
                <div>
                  <h6>
                    <FormattedMessage
                      id={
                        providerCode === AIRLINES.AS
                          ? 'editPolicy.durationShort.AS'
                          : 'editPolicy.durationShort'
                      }
                    />
                  </h6>
                  <div className={classes.flightLengthBox}>
                    <span>
                      <FormattedMessage id="editPolicy.durationShortLabel" />
                    </span>
                    <Field
                      placeholder="shortDurationValue"
                      type="select"
                      name="shortDurationValue"
                      id="shortDurationValue"
                    >
                      {({ form }) => (
                        <div>
                          <UCSelect
                            styles={lengthSelect}
                            disabled={!hasMediumFlight}
                            options={
                              hasMediumFlight
                                ? flightDurationOptions.filter(
                                    (option) => option.value < values.longDurationValue,
                                  )
                                : flightDurationOptions
                            }
                            value={
                              hasMediumFlight ? values.shortDurationValue : values.longDurationValue
                            }
                            defaultValue={flightDurationOptions.find(
                              (item) => item.value === values.shortDurationValue,
                            )}
                            onChange={(item) => {
                              form.setFieldValue('shortDurationValue', item.value);
                            }}
                          />
                        </div>
                      )}
                    </Field>
                    <span>
                      <FormattedMessage id="editPolicy.durationHours" />
                    </span>
                  </div>
                </div>
                {hasMediumFlight && (
                  <div>
                    <h6>
                      <FormattedMessage
                        id={
                          providerCode === AIRLINES.AS
                            ? 'editPolicy.durationMedium.AS'
                            : 'editPolicy.durationMedium'
                        }
                      />
                    </h6>
                    <div className={classes.flightLengthBox}>
                      <span>
                        <FormattedMessage id="editPolicy.durationMediumLabel" />
                      </span>
                      <div className={classes.flightLengthMedium}>
                        <FormattedMessage
                          id="editPolicy.durationMediumAnd"
                          values={{
                            firstHour: values.shortDurationValue,
                            lastHour: values.longDurationValue,
                          }}
                        />
                      </div>
                      <span>
                        <FormattedMessage id="editPolicy.durationHours" />
                      </span>
                    </div>
                  </div>
                )}
                <div>
                  <h6>
                    <FormattedMessage
                      id={
                        providerCode === AIRLINES.AS
                          ? 'editPolicy.durationLong.AS'
                          : 'editPolicy.durationLong'
                      }
                    />
                  </h6>
                  <div className={classes.flightLengthBox}>
                    <span>
                      <FormattedMessage id="editPolicy.durationLongLabel" />
                    </span>
                    <Field
                      placeholder="longDurationValue"
                      type="select"
                      name="longDurationValue"
                      id="longDurationValue"
                    >
                      {({ form }) => (
                        <div>
                          <UCSelect
                            styles={lengthSelect}
                            options={
                              hasMediumFlight
                                ? flightDurationOptions.filter(
                                    (option) => option.value > values.shortDurationValue,
                                  )
                                : flightDurationOptions
                            }
                            value={values.longDurationValue}
                            defaultValue={flightDurationOptions.find(
                              (item) => item.value === values.longDurationValue,
                            )}
                            onChange={(item) => {
                              form.setFieldValue('longDurationValue', item.value);
                              !hasMediumFlight &&
                                form.setFieldValue('shortDurationValue', item.value);
                            }}
                          />
                        </div>
                      )}
                    </Field>
                    <span>
                      <FormattedMessage id="editPolicy.durationHours" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        <div className={classes.flightTypeWrapper}>
          <div className={classes.flightType}>
            <FormattedMessage
              id={
                providerCode === AIRLINES.AS
                  ? 'editPolicy.durationShortTitle.AS'
                  : 'editPolicy.durationShortTitle'
              }
            />
            <span className={classes.flightDot}></span>
            <FormattedMessage
              id="editPolicy.durationShortTime"
              values={{
                hour: flightDurationOptions[
                  (hasMediumFlight ? values.shortDurationValue : values.longDurationValue) - 1
                ]?.label,
              }}
            />
          </div>
          {userHasEditPermission && (
            <>
              {!editDuration && (
                <UCButton
                  type="defaultLink"
                  className={classes.btnContainer}
                  onClick={() => setEditDuration(true)}
                  text={
                    <FormattedMessage
                      id={
                        providerCode === AIRLINES.AS
                          ? 'editPolicy.durationEditBtn.AS'
                          : 'editPolicy.durationEditBtn'
                      }
                    />
                  }
                />
              )}
            </>
          )}
        </div>
        <div className={classes.policyCardRow}>
          <div className={classes.policyRowName}>
            <h6>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.durationCabinTitle.AS'
                    : 'editPolicy.durationCabinTitle'
                }
              />
            </h6>
            <div className={classes.policyTextLink}>
              <p
                dangerouslySetInnerHTML={{
                  __html: durationCabinSubtitle,
                }}
              />
              {/* <Link to={"#"} target="_blank"> */}
              {/* <FormattedMessage id="editPolicy.durationCabinLink" /> */}
              {/* </Link> */}
            </div>
          </div>
          <div className={classes.policyRowAction}>
            <Field
              placeholder="shortCabinOptionValue"
              type="select"
              name="shortCabinOptionValue"
              id="shortCabinOptionValue"
            >
              {({ form }) => (
                <div className={classes.selectFieldWrapper}>
                  <UCSelect
                    styles={selectField}
                    className={classes.selectField}
                    options={aaPolicyCabinOptions}
                    value={values.shortCabinOptionValue}
                    defaultValue={aaPolicyCabinOptions.find(
                      (item) => item.value === values.shortCabinOptionValue,
                    )}
                    onChange={(item) => form.setFieldValue('shortCabinOptionValue', item.value)}
                  />
                </div>
              )}
            </Field>
          </div>
        </div>
        <hr className={classes.divider} />
        {hasMediumFlight && (
          <>
            <div className={classes.flightTypeWrapper}>
              <div className={classes.flightType}>
                <FormattedMessage
                  id={
                    providerCode === AIRLINES.AS
                      ? 'editPolicy.durationMediumTitle.AS'
                      : 'editPolicy.durationMediumTitle'
                  }
                />
                <span className={classes.flightDot}></span>
                <FormattedMessage
                  id="editPolicy.durationMediumTime"
                  values={{
                    firstHour: values.shortDurationValue,
                    lastHour: values.longDurationValue,
                  }}
                />
              </div>
            </div>
            <div className={classes.policyCardRow}>
              <div className={classes.policyRowName}>
                <h6>
                  <FormattedMessage
                    id={
                      providerCode === AIRLINES.AS
                        ? 'editPolicy.durationCabinTitle.AS'
                        : 'editPolicy.durationCabinTitle'
                    }
                  />
                </h6>
                <div className={classes.policyTextLink}>
                  <p
                    dangerouslySetInnerHTML={{
                      __html: durationCabinSubtitle,
                    }}
                  />
                  {/* <Link to={"#"} target="_blank"> */}
                  {/* <FormattedMessage id="editPolicy.durationCabinLink" /> */}
                  {/* </Link> */}
                </div>
              </div>
              <div className={classes.policyRowAction}>
                <Field
                  placeholder="mediumCabinOptionValue"
                  type="select"
                  name="mediumCabinOptionValue"
                  id="mediumCabinOptionValue"
                >
                  {({ form }) => (
                    <div className={classes.selectFieldWrapper}>
                      <UCSelect
                        styles={selectField}
                        className={classes.selectField}
                        options={aaPolicyCabinOptions}
                        value={values.mediumCabinOptionValue}
                        defaultValue={aaPolicyCabinOptions.find(
                          (item) => item.value === values.mediumCabinOptionValue,
                        )}
                        onChange={(item) =>
                          form.setFieldValue('mediumCabinOptionValue', item.value)
                        }
                      />
                    </div>
                  )}
                </Field>
              </div>
            </div>
            <hr className={classes.divider} />
          </>
        )}
        <div className={classes.flightTypeWrapper}>
          <div className={classes.flightType}>
            <FormattedMessage
              id={
                providerCode === AIRLINES.AS
                  ? 'editPolicy.durationLongTitle.AS'
                  : 'editPolicy.durationLongTitle'
              }
            />
            <span className={classes.flightDot}></span>
            <FormattedMessage
              id="editPolicy.durationLongTime"
              values={{
                hour: flightDurationOptions[values.longDurationValue - 1]?.label,
              }}
            />
          </div>
        </div>
        <div className={classes.policyCardRow}>
          <div className={classes.policyRowName}>
            <h6>
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'editPolicy.durationCabinTitle.AS'
                    : 'editPolicy.durationCabinTitle'
                }
              />
            </h6>
            <div className={classes.policyTextLink}>
              <p
                dangerouslySetInnerHTML={{
                  __html: durationCabinSubtitle,
                }}
              />
              {/* <Link to={"#"} target="_blank"> */}
              {/* <FormattedMessage id="editPolicy.durationCabinLink" /> */}
              {/* </Link> */}
            </div>
          </div>
          <div className={classes.policyRowAction}>
            <Field
              placeholder="longCabinOptionValue"
              type="select"
              name="longCabinOptionValue"
              id="longCabinOptionValue"
            >
              {({ form }) => (
                <div className={classes.selectFieldWrapper}>
                  <UCSelect
                    styles={selectField}
                    className={classes.selectField}
                    options={aaPolicyCabinOptions}
                    value={values.longCabinOptionValue}
                    defaultValue={aaPolicyCabinOptions.find(
                      (item) => item.value === values.longCabinOptionValue,
                    )}
                    onChange={(item) => form.setFieldValue('longCabinOptionValue', item.value)}
                  />
                </div>
              )}
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlightDurationCard;
